import { Injectable, inject, signal } from '@angular/core';
import { AuthService } from '../auth/auth.service';
import { environment } from '../../../../environments/environment';

// Modules gelés quand COMPNL est CLOS
const COMPNL_MODULES = ['joueurs', 'clubs', 'dirigeants', 'staff', 'arbitres'];
// Actions transfert bloquées quand COMPT est CLOS
const COMPT_BLOCKED_ACTIONS = ['transfer_initiate', 'transfer_accept', 'transfer_cancel', 'transfer_fed_accept', 'transfer_fed_reject'];
// Actions autorisées quand action_block = true (lecture + export uniquement)
const ALLOWED_WHEN_BLOCKED: string[] = ['read', 'export'];

@Injectable({ providedIn: 'root' })
export class PermissionService {
    private authService = inject(AuthService);

    // Signal: which campaign types are frozen
    private _frozenCampaigns = signal<{ compnl: boolean; compt: boolean }>({ compnl: false, compt: false });

    /**
     * Called by SidebarComponent after loading licence periods.
     * Sets which campaign types are currently CLOS.
     */
    setFrozenCampaigns(licencePeriods: any[]): void {
        const isOpen = (p: any): boolean => {
            const s = String(p?.licenceperiod_status_code || '').trim().toLowerCase();
            return s === 'open';
        };

        const compnlPeriods = licencePeriods.filter(p => p.licenceperiod_type === 'COMPNL');
        const comptPeriods  = licencePeriods.filter(p => p.licenceperiod_type === 'COMPT');

        const compnlFrozen = compnlPeriods.length > 0 && !compnlPeriods.some(isOpen);
        const comptFrozen  = comptPeriods.length  > 0 && !comptPeriods.some(isOpen);

        this._frozenCampaigns.set({ compnl: compnlFrozen, compt: comptFrozen });
    }

    /**
     * Check if current user has a specific permission.
     * - Si environment.action_block = true → seulement read/export/stats autorisés
     * - Si campagne CLOS → seulement read autorisé pour les modules concernés
     */
    can(project: string, module: string, action: string): boolean {
        const perms = this.authService.getUser()?.permissions;
        if (!perms) return false;
        const key = `${project}:${module}`;
        if (!(perms[key]?.includes(action) ?? false)) return false;

        // action_block: bloc global — seulement read/export/stats autorisés
        if (environment.action_block && !ALLOWED_WHEN_BLOCKED.includes(action)) {
            return false;
        }

        // Campagne COMPNL CLOS: seulement read autorisé
        const frozen = this._frozenCampaigns();
        if (frozen.compnl && project === 'licence' && COMPNL_MODULES.includes(module) && action !== 'read') {
            return false;
        }

        // Campagne COMPT CLOS: transferts bloqués
        if (frozen.compt && project === 'licence' && module === 'joueurs' && COMPT_BLOCKED_ACTIONS.includes(action)) {
            return false;
        }

        return true;
    }

    // Priority-ordered list of "club-area" landing routes. /club (the club dashboard) needs
    // clubs:read, which not every club-scoped role has (e.g. CLUB_REFE only has arbitres:read)
    // — landing everyone on /club regardless was the actual cause of narrower roles bouncing
    // to /unauthorized right after login. This lands each account on the first route its own
    // permissions actually let it use, instead of a route hardcoded for the broadest role.
    private readonly CLUB_LANDING_PRIORITY: Array<{ module: string; route: string }> = [
        { module: 'clubs', route: '/club' },
        { module: 'arbitres', route: '/Arbitres' },
        { module: 'staff', route: '/Staff' },
        { module: 'joueurs', route: '/Joueurs' },
        { module: 'dirigeants', route: '/Dirigeants' },
        { module: 'formulaires', route: '/Formulaires' },
    ];

    /** First club-area route the current user's permissions actually grant read access to. */
    getClubLandingRoute(): string {
        for (const { module, route } of this.CLUB_LANDING_PRIORITY) {
            if (this.can('licence', module, 'read')) return route;
        }
        return '/unauthorized';
    }

    hasAnyPermission(project: string): boolean {
        const perms = this.authService.getUser()?.permissions;
        if (!perms) return false;
        return Object.keys(perms).some(k => k.startsWith(`${project}:`));
    }

    getActions(project: string, module: string): string[] {
        const perms = this.authService.getUser()?.permissions;
        if (!perms) return [];
        return perms[`${project}:${module}`] ?? [];
    }

    get frozenCampaigns() {
        return this._frozenCampaigns.asReadonly();
    }

    get isCompnlFrozen(): boolean {
        return this._frozenCampaigns().compnl;
    }

    get isComptFrozen(): boolean {
        return this._frozenCampaigns().compt;
    }

    get isActionBlocked(): boolean {
        return environment.action_block === true;
    }
}
